import React from 'react'
import { MenuType, Legend, ObservatoryType, Geographic, RoadType, MoblieLocationType } from 'common/types'
import dayjs from 'dayjs'
import { isDevelopment } from 'common/utils'
import PolePng from 'assets/suijin/suijin-chart-pole.png'
import carPng from 'assets/suijin/suijin-chart-car.png'
import { isPrivateMode, isPublicMode, isYosokuMode } from 'common/config'
import { LAYERS } from 'common/layers'

type monitorContainerType = 'stage' | 'monitor' | 'underpass' | 'none'

type AppContextType = {
  baseDate: dayjs.Dayjs
  setBaseDate: React.Dispatch <React.SetStateAction<dayjs.Dayjs>>

  menu: MenuType
  setMenu: React.Dispatch <React.SetStateAction<MenuType>>

  observatoryTypes: ObservatoryType[]
  setObservatoryTypes: React.Dispatch <React.SetStateAction<ObservatoryType[]>>

  observatoryContainer: boolean
  setObservatoryContainer: React.Dispatch <React.SetStateAction<boolean>>

  monitorContainer: monitorContainerType
  setMonitorContainer: React.Dispatch <React.SetStateAction<monitorContainerType>>

  rightContainer: boolean
  setRightContainer: React.Dispatch <React.SetStateAction<boolean>>

  hazardMapIds: string[]
  setHazardMapIds: React.Dispatch <React.SetStateAction<string[]>>

  hazardMapLegends: Legend[]
  setHazardMapLegends: React.Dispatch <React.SetStateAction<Legend[]>>

  roadRegulation: boolean
  setRoadRegulation: React.Dispatch <React.SetStateAction<boolean>>

  roadRegulationTypes: RoadType[]
  setRoadRegulationTypes: React.Dispatch <React.SetStateAction<RoadType[]>>

  estimatedFloodingLayers: string[]
  setEstimatedFloodingLayers: React.Dispatch <React.SetStateAction<string[]>>

  geographics: Geographic[]
  setGeographics: React.Dispatch <React.SetStateAction<Geographic[]>>

  loading: boolean
  setLoading: React.Dispatch <React.SetStateAction<boolean>>

  snackbarMessage: string
  setSnackbarMessage: React.Dispatch <React.SetStateAction<string>>

  images: { suijinChartCar?: HTMLImageElement, suijinChartPole?: HTMLImageElement }
  setImages: React.Dispatch <React.SetStateAction<{ suijinChartCar?: HTMLImageElement, suijinChartPole?: HTMLImageElement }>>

  riverFloodsSensor: boolean
  setRiverFloodsSensor: React.Dispatch <React.SetStateAction<boolean>>

  hinanData: { [key: string]: any[] }
  setHinanData: React.Dispatch <React.SetStateAction<{ [key: string]: any[] }>>

  mobileLocationAddMode: boolean
  setMobileLocationAddMode: React.Dispatch <React.SetStateAction<boolean>>

  mobileLocations: MoblieLocationType[]
  setMobileLocations: React.Dispatch <React.SetStateAction<MoblieLocationType[]>>

  getFormattedBaseDate: (roundMinutes?: number) => string
  loadImages: () => Promise<void>
}

export const AppContext = React.createContext<AppContextType | undefined>(undefined)

export const AppProvider = ({children}: React.PropsWithChildren) => {
  const [baseDate, setBaseDate] = React.useState<dayjs.Dayjs>(/*isDevelopment ? dayjs.utc('2026/05/26 12:00', 'YYYY/MM/DD HH:mm') : */ dayjs.utc())
  const [menu, setMenu] = React.useState<MenuType>('river-floods')
  const [observatoryTypes, setObservatoryTypes] = React.useState<ObservatoryType[]>(() => {
    if (isPublicMode || isYosokuMode) {
      return [ObservatoryType.Suijin]
    }
    const obsTypes = localStorage.getItem('observatoryTypes')
    if (obsTypes) {
      return JSON.parse(obsTypes) as ObservatoryType[]
    } else {
      return [ObservatoryType.Stage, ObservatoryType.Rain, ObservatoryType.Camera, ObservatoryType.Tide, ObservatoryType.Pump, ObservatoryType.Suijin]
    }
  })
  const [observatoryContainer, setObservatoryContainer] = React.useState<boolean>(false)
  const [monitorContainer, setMonitorContainer] = React.useState<monitorContainerType>(isPrivateMode ? 'stage' : 'none')
  const [rightContainer, setRightContainer] = React.useState<boolean>(false)
  const [hazardMapIds, setHazardMapIds] = React.useState<string[]>([])
  const [hazardMapLegends, setHazardMapLegends] = React.useState<Legend[]>([])
  const [roadRegulation, setRoadRegulation] = React.useState<boolean>(isPublicMode ? false : localStorage.getItem('roadRegulationLayerVisible') === 'visible')
  const [roadRegulationTypes, setRoadRegulationTypes] = React.useState<RoadType[]>([RoadType.TENKI, RoadType.KOUJI, RoadType.YOKOKU, RoadType.ETC])
  const [estimatedFloodingLayers, setEstimatedFloodingLayers] = React.useState<string[]>(() => {
    return [
      LAYERS.HINAN_AREA_ALL.id,
      LAYERS.SCHOOL_AREA_RISK.id,
      LAYERS.RIVER.id,
      LAYERS.EF_SHINSUISOUTEI.id,
      // LAYERS.EF_TOKUNICHUUI.id,
      LAYERS.EF_SHINSUILATEST.id,
    ]
  })
  const [geographics, setGeographics] = React.useState<Geographic[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>('')
  const [images, setImages] = React.useState<{ suijinChartCar?: HTMLImageElement, suijinChartPole?: HTMLImageElement }>({})
  const [riverFloodsSensor, setRiverFloodsSensor] = React.useState<boolean>(false)
  const [hinanData, setHinanData] = React.useState<{ [key: string]: any[] }>({})
  const [mobileLocationAddMode, setMobileLocationAddMode] = React.useState<boolean>(false)
  const [mobileLocations, setMobileLocations] = React.useState<MoblieLocationType[]>(() => {
    const locations = localStorage.getItem('mobileLocations')
    if (locations) {
      try {
        return JSON.parse(locations) as MoblieLocationType[]
      } catch (e) {
        console.error('Failed to parse mobile locations from localStorage:', e)
        return []
      }
    } else {
      return []
    }
  })

  const getFormattedBaseDate = (roundMinutes?: number) => {
    if (roundMinutes && roundMinutes > 0) {
      const targetDate = baseDate.subtract(baseDate.minute() % roundMinutes, 'minutes')
      return targetDate.format('YYYY/MM/DD HH:mm')
    } else {
      return baseDate.format('YYYY/MM/DD HH:mm')
    }
  }

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = src
      img.onload = () => resolve(img)
      img.onerror = (err) => reject(err)
    })
  }

  const loadImages = async () => {
    const carImg = await loadImage(carPng)
    const poleImg = await loadImage(PolePng)
    setImages({ suijinChartCar: carImg, suijinChartPole: poleImg })
  }

  const value = React.useMemo(() => ({
    baseDate, setBaseDate,
    menu, setMenu,
    observatoryTypes, setObservatoryTypes,
    observatoryContainer, setObservatoryContainer,
    monitorContainer, setMonitorContainer,
    rightContainer, setRightContainer,
    hazardMapIds, setHazardMapIds,
    hazardMapLegends, setHazardMapLegends,
    roadRegulation, setRoadRegulation,
    roadRegulationTypes, setRoadRegulationTypes,
    estimatedFloodingLayers, setEstimatedFloodingLayers,
    geographics, setGeographics,
    loading, setLoading,
    snackbarMessage, setSnackbarMessage,
    images, setImages,
    riverFloodsSensor, setRiverFloodsSensor,
    hinanData, setHinanData,
    mobileLocationAddMode, setMobileLocationAddMode,
    mobileLocations, setMobileLocations,
    getFormattedBaseDate,
    loadImages,
  }), [
    baseDate, setBaseDate,
    menu, setMenu,
    observatoryTypes, setObservatoryTypes,
    observatoryContainer, setObservatoryContainer,
    monitorContainer, setMonitorContainer,
    rightContainer, setRightContainer,
    hazardMapIds, setHazardMapIds,
    hazardMapLegends, setHazardMapLegends,
    roadRegulation, setRoadRegulation,
    roadRegulationTypes, setRoadRegulationTypes,
    estimatedFloodingLayers, setEstimatedFloodingLayers,
    geographics, setGeographics,
    loading, setLoading,
    snackbarMessage, setSnackbarMessage,
    images, setImages,
    riverFloodsSensor, setRiverFloodsSensor,
    hinanData, setHinanData,
    mobileLocationAddMode, setMobileLocationAddMode,
    mobileLocations, setMobileLocations,
    getFormattedBaseDate,
    loadImages,
  ])

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}
